import { Injectable } from "@angular/core";
import {
  Router,
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import { PaymentsService } from "../services/payments.service";
import { UserService } from "./user.service";

/**
 * Blocks the authenticated app (landing-page, plan, settings, etc.) until the current
 * account has an active Stripe subscription. Runs alongside AuthenticationGuard on the
 * top-level guarded route block - AuthenticationGuard already ensures a valid session
 * exists before this guard's subscription-status check runs.
 */
@Injectable()
export class SubscriptionGuard implements CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private paymentsService: PaymentsService,
    private userService: UserService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    let active = false;
    try {
      active = await this.paymentsService.isSubscriptionActive();
    } catch {
      // The subscription check round-trips to the API, so a stale/invalid token
      // (e.g. an old session the server no longer accepts → 401/500) surfaces here as a
      // thrown error. active stays false and is handled below.
      active = false;
    }

    if (active) {
      return true;
    }

    // We get here in two cases, and both are rejected to /login:
    //   1. The session couldn't be confirmed (the check threw).
    //   2. A valid session with no active subscription.
    // This matches login.component.authorizeUser, which logs out an unpaid account and
    // returns it to login rather than parking it on /stripe-payments. New/lapsed users
    // reach /stripe-payments through the register → subscribe flow, never via this guard.
    this.userService.logoutOnTokenExp();
    this.router.navigate(["/login"]);
    return false;
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    return this.canActivate(childRoute, state);
  }
}
